import React, { useState, useEffect, useCallback } from "react";
import { Field, FormikErrors, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import { BaseExpenseFieldsProps, ExpenseEntry, ExpenseFormData } from "./types";
import { Parking, parkingApi } from "../../../services/api";
import { formatInputCurrency } from "../../../utils/utils";
import { useParams } from "react-router-dom";
import { masterDataAllocationApi } from "../../../project/project_module/services/apiService";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import { Checkbox, FormControlLabel } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

// Debounce hook for search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function ParkingExpenseFields({
  index,
  isProjectExpense = false,
  isTripExpense = false,
}: BaseExpenseFieldsProps) {
  const { projectId } = useParams();
  const { values, setFieldValue, errors } = useFormikContext<ExpenseFormData>();
  const [parkingOptions, setParkingOptions] = useState<any[]>([]);
  const [selectedParking, setSelectedParking] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;
  const [addNotes, setAddNotes] = useState<boolean>(false);
  const [showParkingDetails, setShowParkingDetails] = useState<boolean>(false);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Search parking data
  const searchParkingData = useCallback(async (term: string) => {
    if (term.length < 2 && term.length > 0) return;

    setIsLoading(true);
    setSearchError("");

    try {
      const data = await parkingApi.search(term);
      setParkingOptions(data);
    } catch (error) {
      setSearchError("Failed to load parking data");
      console.error("Error searching parking:", error);
      setParkingOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const searchProjectParkingData = useCallback(async (term: string) => {
    if (term.length < 2 && term.length > 0) return;

    setIsLoading(true);
    setSearchError("");

    try {
      const data =
        await masterDataAllocationApi.getMasterDataAllocationsByProject(
          projectId!,
          "parking"
        );
      setParkingOptions(
        data?.map((parking) => parking.masterData) as Parking[]
      );
    } catch (error) {
      setSearchError("Failed to load parking data");
      console.error("Error searching parking:", error);
      setParkingOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to search when debounced term changes
  useEffect(() => {
    if (isProjectExpense) {
      searchProjectParkingData(debouncedSearchTerm);
    } else {
      searchParkingData(debouncedSearchTerm);
    }
  }, [
    debouncedSearchTerm,
    searchParkingData,
    searchProjectParkingData,
    isProjectExpense,
  ]);

  // Effect to ensure category is set on component mount
  useEffect(() => {
    const expense = values.expenses[index];
    if (expense && (!expense.category || expense.category !== "PARKING")) {
      setFieldValue(`expenses.${index}.category`, "PARKING");
    }
  }, [index, setFieldValue]); // Only run when index changes (component mount)

  // Initialize state from form values (for edit mode)
  useEffect(() => {
    const expense = values.expenses[index];
    if (expense) {
      // Ensure category is always set to PARKING for parking expenses
      if (!expense.category || expense.category !== "PARKING") {
        setFieldValue(`expenses.${index}.category`, "PARKING");
      }
      
      // Initialize manual entry mode if referenceId is empty but paidTo is filled
      if (!expense.referenceId && expense.paidTo) {
        setIsManualEntry(true);
      }
      
      // Try to find and set selected parking if referenceId exists
      if (expense.referenceId && parkingOptions.length > 0) {
        const parking = parkingOptions.find(
          (p) => p.id === expense.referenceId
        );
        if (parking) {
          setSelectedParking(parking);
          setIsManualEntry(false);
        }
      }
    }
  }, [values.expenses, index, parkingOptions, setFieldValue]);

  // Handle parking selection
  const handleParkingChange = (parking: any | null, inputValue?: string) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setSelectedParking(parking);

    if (parking) {
      // Auto-fill fields from master data
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.isManualEntry`, false);
      setFieldValue(`expenses.${index}.referenceId`, parking.id);
      setFieldValue(`expenses.${index}.paidTo`, parking.name);
      setFieldValue(`expenses.${index}.contactNumber`, parking.contactNumber);
      setFieldValue(`expenses.${index}.location`, parking.location);
    } else if (inputValue && inputValue.trim() !== "") {
      // Manual entry mode - generate UUID and set manual entry flag
      setIsManualEntry(true);
      const newUUID = uuidv4();
      setFieldValue(`expenses.${index}.isManualEntry`, true);
      setFieldValue(`expenses.${index}.referenceId`, newUUID);
      setFieldValue(`expenses.${index}.paidTo`, inputValue.trim());
    } else {
      // Clear fields
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.isManualEntry`, false);
      setFieldValue(`expenses.${index}.referenceId`, "");
      setFieldValue(`expenses.${index}.paidTo`, "");
      setFieldValue(`expenses.${index}.contactNumber`, "");
      setFieldValue(`expenses.${index}.location`, "");
    }
  };

  // Get option label
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return `${option.name} - ${option.location}`;
  };

  // Render parking option
  const renderParkingOption = (props: any, option: any) => (
    <Box component="li" {...props} sx={{ fontSize: "0.75rem", py: 0.5 }}>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {option.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.75rem", mt: 0.25 }}
        >
          {option.location}
        </Typography>
        {option.contactNumber && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}
          >
            Contact: {option.contactNumber}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        setFieldValue(`expenses.${index}.payments`, [
          ...values.expenses[index].payments!,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        setFieldValue(`expenses.${index}.payments`, []);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  return (
    <Grid container item xs={12} spacing={1}>
      {/* Date */}
      {!isTripExpense && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            label="Date"
            type="date"
            name={`expenses.${index}.expenseDate`}
            fullWidth
            required
            {...commonDateFieldProps}
          />
        </Grid>
      )}

      {/* Parking Type Label */}
      {/* <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "40px",
            px: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          <LocalParkingIcon color="primary" sx={{ fontSize: "1rem" }} />
          <Typography
            variant="body1"
            color="primary"
            sx={{ fontSize: "0.75rem", fontWeight: 600 }}
          >
            Parking Expense
          </Typography>
        </Box>
      </Grid> */}

      {/* Parking Selection Autocomplete */}
      <Grid container item xs={12} sm={3}>
        <Grid
          item
          xs={selectedParking ? 10.5 : 12}
          sm={selectedParking ? 10.5 : 12}
        >
          <Autocomplete
            freeSolo
            options={parkingOptions}
            loading={isLoading}
            getOptionLabel={getOptionLabel}
            value={selectedParking}
            onChange={(_, newValue, reason) => {
              if (reason === "selectOption" && typeof newValue === "object") {
                // Selected from dropdown - reset manual entry mode
                setIsManualEntry(false);
                setFieldValue(`expenses.${index}.isManualEntry`, false);
                handleParkingChange(newValue);
              } else if (reason === "clear") {
                // Clear selection - reset all fields
                setIsManualEntry(false);
                setFieldValue(`expenses.${index}.isManualEntry`, false);
                handleParkingChange(null);
              }
            }}
            onInputChange={(_, inputValue, reason) => {
              setSearchTerm(inputValue);

              if (reason === "input") {
                const matchingOption = parkingOptions.find((option) =>
                  getOptionLabel(option)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                );

                if (!matchingOption && inputValue.trim() !== "") {
                  // Manual entry - generate UUID and set manual entry flag
                  setIsManualEntry(true);
                  const newUUID = uuidv4();
                  setFieldValue(`expenses.${index}.isEdited`, true);
                  setFieldValue(`expenses.${index}.isManualEntry`, true);
                  setFieldValue(`expenses.${index}.referenceId`, newUUID);
                  setFieldValue(`expenses.${index}.paidTo`, inputValue.trim());
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...commonTextFieldProps}
                label="Select Parking"
                placeholder="Search from master data or enter manually..."
                required
                fullWidth
                error={!!searchError || !!expenseErrors?.paidTo}
                helperText={
                  searchError
                    ? searchError
                    : expenseErrors?.paidTo
                    ? "Parking location name is required"
                    : "Select from dropdown or type to add new parking location"
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={renderParkingOption}
            noOptionsText={
              isLoading
                ? "Searching..."
                : searchError
                ? "Error loading options"
                : "No matching parking found. You can still type to add manually."
            }
          />
        </Grid>
        {selectedParking && (
          <Grid item xs={12} sm={1.5}>
            <IconButton
              title="Show Parking Details"
              onClick={() => setShowParkingDetails(!showParkingDetails)}
            >
              <Visibility
                fontSize="small"
                color="primary"
                sx={{ fontSize: "1rem" }}
              />
            </IconButton>
          </Grid>
        )}
      </Grid>

      {/* Manual Entry Alert */}
      {/* {isManualEntry && (
        <Grid item xs={12}>
          <Alert
            severity="info"
            sx={{ mb: 1.5, "& .MuiAlert-message": { fontSize: "0.75rem" } }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              <strong>Manual Entry Mode:</strong> This parking location is not
              in our master data. Please fill in the details manually below.
            </Typography>
          </Alert>
        </Grid>
      )} */}

      {/* Auto-filled Parking Details */}
      {selectedParking && !isManualEntry && showParkingDetails && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "primary.light",
              bgcolor: "#F0F7FF",
              mb: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary.dark"
              sx={{ fontSize: "0.75rem" }}
              gutterBottom
            >
              Parking Details (From Master Data)
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Name: {selectedParking.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Location: {selectedParking.location}
                </Typography>
              </Grid>
              {selectedParking.contactNumber && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Contact: {selectedParking.contactNumber}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {/* Paid To Field (manual entry) */}
      {(isManualEntry ||
        values.expenses[index]?.isManualEntry ||
        (!selectedParking && values.expenses[index]?.paidTo)) && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            label="Paid To"
            name={`expenses.${index}.paidTo`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.paidTo`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            required
            placeholder="Enter parking location name"
            {...commonTextFieldProps}
          />
        </Grid>
      )}

      {/* Amount */}
      <Grid item xs={12} sm={3}>
        <Field name={`expenses.${index}.amount`}>
          {({ field, meta }: any) => (
            <TextField
              {...field}
              {...commonTextFieldProps}
              label="Amount"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              value={field.value ? formatInputCurrency(field.value) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: value,
                    },
                  });
                  setFieldValue(`expenses.${index}.isEdited`, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    component="span"
                    sx={{ mr: 0.5, fontSize: "0.75rem" }}
                  >
                    ₹
                  </Typography>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        </Field>
      </Grid>

      {/* Contact Number (manual entry) */}
      {(isManualEntry ||
        values.expenses[index]?.isManualEntry ||
        (!selectedParking && values.expenses[index]?.contactNumber)) && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            label="Contact Number"
            name={`expenses.${index}.contactNumber`}
            fullWidth
            placeholder="Enter contact number (optional)"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.contactNumber`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            {...commonTextFieldProps}
            inputProps={{
              maxLength: 10,
            }}
            error={Boolean(
              expenseErrors?.contactNumber && expenseErrors?.contactNumber
            )}
            helperText={
              expenseErrors?.contactNumber && expenseErrors?.contactNumber
            }
          />
        </Grid>
      )}

      {/* Location (manual entry or when not auto-filled) */}
      {(isManualEntry ||
        values.expenses[index]?.isManualEntry ||
        (!selectedParking && values.expenses[index]?.location)) && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            label="Location"
            name={`expenses.${index}.location`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.location`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            error={Boolean(expenseErrors?.location)}
            helperText={expenseErrors?.location}
            required
            placeholder="Enter parking location"
            {...commonTextFieldProps}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => setAddNotes(!addNotes)}
          size={smallButtonProp.size}
          sx={{
            ...smallButtonProp.sx,
            color: "grey",
            borderColor: "grey.400",
            "&:hover": {
              borderColor: "grey.400",
              color: "grey",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
            {addNotes ? "Hide Notes" : "Add Notes"}
          </Typography>
        </Button>
        {Number(values.expenses[index].amount) > 0 &&
          values.expenses[index].payments?.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    values.expenses[index].payments?.length! &&
                    values.expenses[index].payments?.length! > 0
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
      </Grid>

      {/* Notes */}
      {addNotes && (
        <Grid item xs={12}>
          <Field
            as={TextField}
            label="Notes"
            name={`expenses.${index}.notes`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.notes`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            multiline
            rows={2}
            placeholder="Any additional notes about this parking expense..."
            {...commonTextFieldProps}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default ParkingExpenseFields;
